/**
 * Client Service — Data access for the client portal.
 *
 * Clients see: their projects, delivered batches, annotation reviews,
 * and project comments. They NEVER see annotator rates, costs, or margins.
 */

import { supabase } from '../lib/supabase';
import { safeQuery } from '../utils/safeSupabase';
import { logAudit, AUDIT_ACTIONS } from './auditService';
// Phase 13 minimal — submitBatchDispute notifies the project's PM via the
// existing notification feed. Imported here (not lazy) because the dispute
// path is a customer-facing trust signal and the notification fire is a
// first-class side effect, not an opportunistic add-on.
import { createNotification } from './notificationService';

export async function resolveClientIdForUser(email) {
  if (!email) return null;

  const { data, error } = await supabase.rpc('resolve_current_client_access');

  if (error) {
    console.error('resolve_current_client_access error:', error);
    return null;
  }

  const row = Array.isArray(data) ? data[0] : data;
  return row?.client_id || null;
}

// ─── Client record access ────────────────────────────────
//
// Phase 11 slot 12 (§8.5 row 3 / PRD-DATA-002): centralizes the direct-table
// reads that ClientPortal.jsx used to inline (`supabase.from('client').select(...)`).
// Routing through clientService keeps every client-portal data path in one
// audited surface — easier to reason about RLS coverage + safeQuery wrapping.
//
// Both helpers below read the CLIENT'S OWN row (filtered by `id = cId`).
// The client RLS policy on `public.client` enforces that callers can only
// see their own row; these helpers are not a security boundary, they're a
// service-layer convenience.

/** Visibility flags (can_view_budget, can_view_hours) for the client portal. */
export async function fetchClientVisibilityFlags(clientId) {
  if (!clientId) return null;

  const { data, error } = await safeQuery(
    supabase.from('client').select('can_view_budget, can_view_hours').eq('id', clientId).single(),
    'client-visibility-flags',
  );

  if (error) {
    console.error('fetchClientVisibilityFlags error:', error.message);
    return null;
  }

  return data || null;
}

/** Identity columns for the client header card (name, contract status, etc.). */
export async function fetchClientRecord(clientId) {
  if (!clientId) return null;

  const { data, error } = await safeQuery(
    supabase
      .from('client')
      .select('id, name, display_name, contact_email, contract_status')
      .eq('id', clientId)
      .single(),
    'client-record',
  );

  if (error) {
    console.error('fetchClientRecord error:', error.message);
    return null;
  }

  return data || null;
}

/**
 * Fetch the minimal job metadata the portal needs to send a notification to
 * the project's creator after a client request submission. Returns null if
 * RLS blocks the read or the row doesn't exist.
 *
 * Phase 11 slot 12 (§8.5 row 3): replaces an inline `supabase.from('job')`
 * read in ClientPortal.jsx — the previous pattern silently swallowed RLS
 * errors, this surfaces them via safeQuery.
 */
export async function fetchJobNotificationMeta(jobId) {
  if (!jobId) return null;

  const { data, error } = await safeQuery(
    supabase.from('job').select('created_by, title').eq('id', jobId).single(),
    'client-job-notif-meta',
  );

  if (error) {
    console.error('fetchJobNotificationMeta error:', error.message);
    return null;
  }

  return data || null;
}

// ─── Projects ─────────────────────────────────────────────

/**
 * Fetch projects visible to a client.
 * Joins job with batch stats and assignment counts.
 * Filters by client_id on the job (matches the client's individual record).
 */
/**
 * Fetch projects visible to a client.
 * If clientId is provided, filters to only that client's jobs.
 * Otherwise falls back to org-wide (admin/god mode use case only).
 */
export async function fetchClientProjects(orgId, clientId = null, options = {}) {
  const { requireClientId = false } = options;

  if (!orgId || (requireClientId && !clientId)) return [];

  const { data, error } = await supabase.rpc('fetch_client_project_summaries', {
    p_org_id: orgId,
    p_client_id: clientId,
  });

  if (error) {
    console.error('fetch_client_project_summaries error:', error);
    return [];
  }

  return (data || []).map(row => ({
    id: row.id,
    title: row.title,
    domain: row.domain,
    status: row.status,
    team_size: row.team_size,
    start_date: row.start_date,
    end_date: row.end_date,
    sla_delivery_date: row.sla_delivery_date,
    created_at: row.created_at,
    client_display_name: row.client_display_name,
    client_name: row.client_name,
    project_manager_id: row.project_manager_id,
    pm_name: row.pm_name,
    pm_email: row.pm_email,
    estimated_total_value: row.estimated_total_value ? Number(row.estimated_total_value) : 0,
    _batchStats: {
      totalItems: Number(row.total_items || 0),
      completedItems: Number(row.completed_items || 0),
      acceptedItems: Number(row.accepted_items || 0),
      rejectedItems: Number(row.rejected_items || 0),
      batchCount: Number(row.batch_count || 0),
      clientVisibleBatches: Number(row.client_visible_batches || 0),
      avgQuality: row.avg_quality != null ? Number(row.avg_quality) : null,
      completionPercent: Number(row.completion_percent || 0),
    },
    can_view_team_composition: !!row.can_view_team_composition,
    can_view_annotator_profiles: !!row.can_view_annotator_profiles,
    can_view_craft_scores: !!row.can_view_craft_scores,
    can_view_budget: !!row.can_view_budget,
    can_view_hours: !!row.can_view_hours,
    _team: Array.isArray(row.team) ? row.team : [],
  }));
}

export async function fetchProjectTeamProfiles(jobId) {
  if (!jobId) return [];

  const { data, error } = await supabase.rpc('fetch_client_project_team_profiles', {
    p_job_id: jobId,
  });

  if (error) {
    console.error('fetch_client_project_team_profiles error:', error);
    return [];
  }

  return data || [];
}

// ─── Deliverables (Batches for client) ────────────────────

/**
 * Fetch delivered/reviewable batches for a job.
 * Client sees every client-visible batch state used on the detail screen.
 */
export async function fetchDeliverables(jobId) {
  if (!jobId) return [];

  const { data, error } = await supabase.rpc('fetch_client_deliverables', {
    p_job_id: jobId,
  });

  if (error) {
    console.error('fetch_client_deliverables error:', error);
    return [];
  }

  return data || [];
}

// ─── Annotation Reviews ───────────────────────────────────

/**
 * Fetch reviews for a batch.
 */
export async function fetchReviewsForBatch(batchId) {
  if (!batchId) return [];

  const { data, error } = await supabase
    .from('annotation_review')
    .select('*')
    .eq('batch_id', batchId)
    .order('annotation_index');

  if (error) { console.error('fetchReviewsForBatch error:', error); return []; }
  return data || [];
}

/**
 * Submit reviews for a batch.
 * Takes array of { annotation_index, status, client_comment }.
 */
export async function submitBatchReview(batchId, _jobId, _orgId, _reviewerId, reviews) {
  if (!batchId || !reviews?.length) return { success: false };

  // Single atomic RPC: inserts reviews + updates batch + creates quality log
  // RPC derives job_id/org_id from the batch (doesn't trust caller-supplied values)
  // RPC checks caller role and client entitlement internally
  const reviewData = reviews.map((r, i) => ({
    annotation_index: r.annotation_index ?? i,
    annotation_external_id: r.annotation_external_id || null,
    status: r.status,
    client_comment: r.client_comment || null,
  }));

  const { data: result, error: rpcError } = await supabase.rpc('submit_client_review', {
    p_batch_id: batchId,
    p_reviews: reviewData,
  });

  if (rpcError) return { success: false, error: rpcError.message };
  if (result && !result.success) return { success: false, error: result.error };

  return { success: true, status: result?.status, accepted: result?.accepted, rejected: result?.rejected };
}

// ─── Project Comments ─────────────────────────────────────

/**
 * Fetch comments for a project.
 */
export async function fetchProjectComments(jobId) {
  if (!jobId) return [];

  const { data, error } = await supabase.rpc('fetch_client_project_comments', {
    p_job_id: jobId,
  });

  if (error) {
    console.error('fetch_client_project_comments error:', error);
    return [];
  }

  return data || [];
}

// ─── Project Requests ─────────────────────────────────────

export async function fetchProjectRequests(jobId) {
  if (!jobId) return [];

  const { data, error } = await supabase.rpc('fetch_client_project_requests', {
    p_job_id: jobId,
  });

  if (error) {
    console.error('fetch_client_project_requests error:', error);
    return [];
  }

  return data || [];
}

export async function createProjectRequest(jobId, requestType, body) {
  if (!jobId || !requestType || !body?.trim()) return null;

  const { data, error } = await supabase.rpc('create_client_project_request', {
    p_job_id: jobId,
    p_request_type: requestType,
    p_body: body.trim(),
  });

  if (error) {
    console.error('create_client_project_request error:', error);
    return null;
  }

  const row = Array.isArray(data) ? data[0] : data;
  return row || null;
}

export async function updateProjectRequestStatus(requestId, status, resolutionNote = '') {
  if (!requestId || !status) return null;

  const { data, error } = await supabase.rpc('update_client_project_request_status', {
    p_request_id: requestId,
    p_status: status,
    p_resolution_note: resolutionNote || null,
  });

  if (error) {
    console.error('update_client_project_request_status error:', error);
    return null;
  }

  const row = Array.isArray(data) ? data[0] : data;
  return row || null;
}

/**
 * Post a comment on a project.
 */
export async function postProjectComment(jobId, orgId, userId, userRole, body) {
  if (!jobId || !body?.trim()) return null;

  const { data, error } = await supabase.rpc('create_client_project_comment', {
    p_job_id: jobId,
    p_body: body.trim(),
  });

  if (error) {
    console.error('create_client_project_comment error:', error);
    return null;
  }

  return data;
}

// ── Structured Client Requests ──────────────────────────────────────

/**
 * Submit a structured client request via RPC.
 * @param {string} jobId
 * @param {string} requestType - one of: additional_items, timeline_change,
 *   staffing_change, scope_increase, status_update, re_review, dispute
 *   (Phase 13 minimal aligned the keys to the DB CHECK constraint values.
 *   `dispute` was added by migration 266 specifically for the client-portal
 *   "Report Issue" button on Accepted batches.)
 * @param {string} title - short description (max 500 chars)
 * @param {string} [note] - optional detailed note (REQUIRED for `dispute`
 *   type — the modal validates min-10-chars before calling this function)
 */
export async function submitClientRequest(jobId, requestType, title, note = null) {
  if (!jobId || !requestType || !title?.trim()) {
    return { success: false, error: 'Missing required fields' };
  }

  const { data, error } = await supabase.rpc('create_client_request', {
    p_job_id: jobId,
    p_request_type: requestType,
    p_title: title.trim(),
    p_note: note?.trim() || null,
  });

  if (error) {
    console.error('[clientService] submitClientRequest failed:', error.message);
    return { success: false, error: error.message };
  }

  // Phase 13 minimal + slot 5 carry-over: audit-trail completeness for the
  // submit path. Fire SUBMIT_CLIENT_REQUEST for every successful submission;
  // additionally fire RAISE_DISPUTE when the submission is a dispute
  // specifically so the audit log surfaces the higher-stakes event for
  // customer-trust visibility. Both fire-and-forget per the existing
  // logAudit pattern — failures don't block the dispute write.
  logAudit(AUDIT_ACTIONS.SUBMIT_CLIENT_REQUEST, {
    jobId,
    requestType,
    requestId: data?.request_id,
    title: title.trim().slice(0, 200),
  });
  if (requestType === 'dispute') {
    logAudit(AUDIT_ACTIONS.RAISE_DISPUTE, {
      jobId,
      requestId: data?.request_id,
      title: title.trim().slice(0, 200),
    });
  }

  return data || { success: false, error: 'No data returned' };
}

/**
 * Convenience wrapper: client raises a dispute on a specific batch. Phase 13
 * minimal — wraps `submitClientRequest` with `request_type='dispute'`,
 * normalizes the title to "Batch #N dispute" for admin-inbox legibility,
 * and fires the PM notification (fail-soft) so the SPL/admin who created
 * the project sees the dispute land in their notification feed without
 * having to poll the inbox.
 *
 * Caller is the ClientPortal "Report Issue" button. Caller validates
 * `reason.length >= 10` BEFORE calling — this function expects already-
 * trimmed input.
 *
 * @param {string} jobId
 * @param {number} batchNumber  — for the admin-inbox title
 * @param {string} reason       — pre-validated detail text
 * @returns {{ success: boolean, requestId?: string, error?: string }}
 */
export async function submitBatchDispute(jobId, batchNumber, reason) {
  if (!jobId || !reason?.trim() || reason.trim().length < 10) {
    return { success: false, error: 'Dispute requires a reason of at least 10 characters' };
  }

  const title = batchNumber
    ? `Batch #${batchNumber} dispute`
    : 'Batch dispute';

  const result = await submitClientRequest(jobId, 'dispute', title, reason);
  if (!result?.success) return result;

  // Notify the project's PM (fail-soft — never block the dispute on
  // notification failure). Uses fetchJobNotificationMeta from slot 12
  // which already routes through safeQuery for RLS-failure visibility.
  try {
    const meta = await fetchJobNotificationMeta(jobId);
    if (meta?.created_by) {
      const reasonExcerpt = reason.trim().slice(0, 140);
      await createNotification(
        meta.created_by,
        'ClientDisputeRaised',
        {
          jobId,
          jobTitle: meta.title,
          batchNumber,
          reasonExcerpt,
          requestId: result.request_id,
        },
        null,
      );
    }
  } catch (err) {
    // Fail-soft: a notification miss is unfortunate but never blocks the
    // dispute. Log + swallow per the existing ClientPortal pattern.
    console.warn('[clientService] submitBatchDispute notification failed:', err?.message || err);
  }

  return result;
}

/**
 * Fetch server-computed ETA for a project (same data source as SPL view).
 *
 * Phase 11 audit-close round 3: migration 261 changed the RPC return shape
 * (was 4 cols: estimated_completion_date / days_remaining / items_per_day /
 * confidence; now 6 cols: total_items / delivered_items / remaining_items /
 * daily_velocity / estimated_completion_date / on_track). This adapter now
 * derives the historical-API fields (daysRemaining / itemsPerDay /
 * confidence) from the new shape so existing UI consumers keep working,
 * and surfaces the new fields (totalItems / deliveredItems / onTrack) for
 * components that want to use them directly.
 */
export async function fetchClientProjectETA(jobId) {
  if (!jobId) return null;
  const { data, error } = await supabase.rpc('fetch_client_project_eta', { p_job_id: jobId });
  if (error || !data?.[0]) return null;
  const row = data[0];
  // Derive daysRemaining from estimated_completion_date - today (same
  // mental model as the old `days_remaining` column the previous RPC
  // emitted directly). If estimated_completion_date is null, daysRemaining
  // is null too.
  let daysRemaining = null;
  if (row.estimated_completion_date) {
    const eta = new Date(row.estimated_completion_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    daysRemaining = Math.round((eta.getTime() - today.getTime()) / 86400000);
  }
  // Derive a coarse confidence label from on_track + daily_velocity. The
  // pre-261 RPC returned 'high' / 'medium' / 'low' / 'none' literals; we
  // approximate with the same buckets driven by the new columns.
  const velocity = row.daily_velocity ? Number(row.daily_velocity) : 0;
  let confidence;
  if (velocity <= 0 || row.estimated_completion_date == null) confidence = 'none';
  else if (row.on_track === false) confidence = 'low';
  else if (velocity >= 5) confidence = 'high';
  else confidence = 'medium';
  return {
    estimatedDate: row.estimated_completion_date,
    daysRemaining,
    itemsPerDay: velocity > 0 ? velocity : null,
    confidence,
    // Phase 11 round 3: new fields surfaced for callers that want them.
    totalItems: row.total_items,
    deliveredItems: row.delivered_items,
    remainingItems: row.remaining_items,
    onTrack: row.on_track,
  };
}

/**
 * Fetch all requests for a job, ordered by newest first.
 *
 * Does a manual bulk-join instead of a PostgREST embed shorthand.
 *
 * Investigation (migrations 205–208, 2026-04-21):
 *   The FK constraints from client_request.{requested_by, responded_by,
 *   resolved_by} → individual.id ARE declared and marked valid=true in
 *   pg_constraint (migration 207 dumped the authoritative state; 208
 *   verified raw-SQL INSERT correctly raises foreign_key_violation on
 *   orphan references). But the Supabase-hosted PostgREST layer:
 *
 *     1. Does not pick up the FKs for embed-shorthand resolution
 *        (select('*, requester:requested_by(...)') still returns
 *        "Could not find a relationship..." schema-cache errors)
 *     2. Does not enforce FKs on REST-API INSERT — rows with orphan
 *        requested_by land in the table despite valid FK constraints.
 *        Suggests the REST connection runs with session_replication_role
 *        set such that trigger-based FK enforcement is bypassed.
 *
 *   This is hosted-platform behavior, not a migration or code bug.
 *   The FKs are still worthwhile: migrations and raw-SQL cleanups get
 *   integrity checks, and if Supabase ever fixes the REST path we
 *   inherit the improvement automatically.
 *
 * This function will stay on the manual-join shape indefinitely. If a
 * future Supabase release emits FK violations from REST INSERTs, we can
 * simplify back to the embed shorthand — but that's a separate polish
 * item, not blocking anything.
 */
export async function fetchClientRequests(jobId) {
  if (!jobId) return [];

  const { data: rows, error } = await supabase
    .from('client_request')
    .select('*')
    .eq('job_id', jobId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[clientService] fetchClientRequests failed:', error.message);
    return [];
  }
  if (!rows?.length) return [];

  const individualIds = new Set();
  for (const r of rows) {
    if (r.requested_by) individualIds.add(r.requested_by);
    if (r.responded_by) individualIds.add(r.responded_by);
  }

  let byId = {};
  if (individualIds.size > 0) {
    const { data: people } = await supabase
      .from('individual')
      .select('id, name')
      .in('id', Array.from(individualIds));
    byId = Object.fromEntries((people || []).map(p => [p.id, p]));
  }

  return rows.map(r => ({
    ...r,
    requester: r.requested_by ? byId[r.requested_by] || null : null,
    responder: r.responded_by ? byId[r.responded_by] || null : null,
  }));
}

/**
 * Respond to a client request (admin/SPL only).
 * @param {string} requestId
 * @param {string} status - one of: in_review, approved, rejected, fulfilled
 * @param {string} [adminResponse] - optional response text
 * @param {string} respondedBy - admin/SPL individual.id
 */
export async function respondToClientRequest(requestId, status, adminResponse = null, respondedBy = null) {
  if (!requestId || !status) return { success: false, error: 'Missing fields' };

  // Phase 13 minimal: read the request_type up-front so we can fire
  // RESOLVE_DISPUTE in addition to RESPOND_TO_CLIENT_REQUEST when the
  // request being resolved is a dispute. Dispute-resolution time is a
  // customer-trust metric we want surfaced in the audit log distinctly
  // from generic request responses.
  let requestType = null;
  try {
    const { data: typeRow } = await safeQuery(
      supabase.from('client_request').select('request_type').eq('id', requestId).single(),
      'client-request-type-pre-respond',
    );
    requestType = typeRow?.request_type || null;
  } catch { /* fail-soft — don't block response on type read */ }

  const { error } = await supabase
    .from('client_request')
    .update({
      status,
      admin_response: adminResponse?.trim() || null,
      responded_by: respondedBy,
      responded_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', requestId);

  if (error) {
    console.error('[clientService] respondToClientRequest failed:', error.message);
    return { success: false, error: error.message };
  }

  // Phase 11 slot 5: admin response on a client_request (status flip + reply).
  logAudit(AUDIT_ACTIONS.RESPOND_TO_CLIENT_REQUEST, {
    entityType: 'client_request',
    entityId: requestId,
    details: {
      newStatus: status,
      requestType,
      hasResponseText: !!adminResponse?.trim(),
      respondedBy: respondedBy || null,
    },
  });

  // Phase 13 minimal: dispute-specific RESOLVE_DISPUTE event when the
  // resolved request is a dispute AND the new status is terminal
  // (approved | rejected | fulfilled — not in_review which is just an
  // ack). Lets us track time-to-resolution distinctly without polluting
  // RESPOND_TO_CLIENT_REQUEST counts for non-disputes.
  if (requestType === 'dispute' && ['approved', 'rejected', 'fulfilled'].includes(status)) {
    logAudit(AUDIT_ACTIONS.RESOLVE_DISPUTE, {
      entityType: 'client_request',
      entityId: requestId,
      details: {
        resolution: status,
        hasResolutionText: !!adminResponse?.trim(),
        respondedBy: respondedBy || null,
      },
    });
  }

  // Notify the client that their request was responded to
  try {
    const { data: request } = await supabase.from('client_request')
      .select('job_id, requested_by, title, job:job_id(client_id, title, org_id)')
      .eq('id', requestId).single();
    if (request?.job?.client_id) {
      const { resolveClientIndividualId } = await import('./notificationService');
      const clientUserId = await resolveClientIndividualId(request.job.client_id);
      if (clientUserId) {
        const { createNotification } = await import('./notificationService');
        createNotification(clientUserId, 'ClientRequestResolved', {
          jobTitle: request.job?.title, requestTitle: request.title, status,
        }, request.job?.org_id);
      }
    }
  } catch (err) {
    console.warn('[clientService] Failed to notify client of request response:', err.message);
  }

  return { success: true };
}

// ── Client Activity History ─────────────────────────────────────────

/**
 * Fetch recent client activity for a job — aggregated from reviews, comments, reports.
 * Returns a timeline sorted newest first.
 */
export async function fetchClientActivityHistory(jobId, limit = 20) {
  if (!jobId) return [];

  // Use SECURITY DEFINER RPC to bypass client RLS on batch/comment/report tables
  const { data, error } = await supabase.rpc('fetch_client_activity', {
    p_job_id: jobId,
    p_limit: limit,
  });

  if (error) {
    console.warn('[clientService] fetch_client_activity RPC failed, falling back to direct queries:', error.message);
    // Fallback: try direct queries (works for admin/god viewing client portal)
    return fetchClientActivityHistoryFallback(jobId, limit);
  }

  return (data || []).map(row => ({
    type: row.event_type,
    icon: row.event_icon,
    label: row.event_label,
    detail: row.event_detail,
    timestamp: row.event_timestamp,
  }));
}

// Fallback for when RPC is unavailable (pre-migration or admin viewing)
async function fetchClientActivityHistoryFallback(jobId, limit) {
  const [commentsRes, batchesRes] = await Promise.all([
    safeQuery(supabase.from('project_comment').select('id, user_role, body, created_at, author:user_id(name)').eq('job_id', jobId).order('created_at', { ascending: false }).limit(20), 'client-activity-comments'),
    // 4-state lifecycle (backbone Phase 1a): client sees activity once work
    // is past In Progress — In Review (pipeline signal), Delivered (awaiting
    // their action), Accepted (signed off).
    safeQuery(supabase.from('annotation_batch').select('batch_number, status, total_items, submitted_at, updated_at').eq('job_id', jobId).eq('is_overlap', false).in('status', ['In Review', 'Delivered', 'Accepted']).order('updated_at', { ascending: false }).limit(20), 'client-activity-batches'),
  ]);
  const timeline = [];
  for (const b of (batchesRes.data || [])) {
    const labels = { 'In Review': '🔍', 'Delivered': '📦', 'Accepted': '🎉' };
    timeline.push({ type: 'batch', icon: labels[b.status] || '📋', label: `Batch #${b.batch_number} ${b.status.toLowerCase()}`, detail: `${b.total_items} items`, timestamp: b.submitted_at || b.updated_at });
  }
  for (const c of (commentsRes.data || [])) {
    const name = c.author?.name?.split(' ')[0] || (c.user_role === 'client' ? 'You' : 'Team');
    timeline.push({ type: 'comment', icon: '💬', label: `${name} commented`, detail: (c.body || '').slice(0, 80), timestamp: c.created_at });
  }
  timeline.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  return timeline.slice(0, limit);
}

// ── Client Satisfaction Feedback ─────────────────────────────────────

/**
 * Submit client satisfaction feedback for a completed project.
 */
export async function submitClientFeedback(jobId, userId, clientId, { ratingValue, feedbackPositive, feedbackImprovement, comment }) {
  if (!jobId || !userId) return { success: false, error: 'Missing required fields' };

  const { data, error } = await supabase.from('client_feedback').insert({
    job_id: jobId,
    user_id: userId,
    client_id: clientId,
    rating_value: ratingValue || null,
    rating: ratingValue || null,
    feedback_positive: feedbackPositive || null,
    feedback_improvement: feedbackImprovement || null,
    comment: comment || null,
  }).select().single();

  if (error) {
    console.error('[clientService] submitClientFeedback failed:', error.message);
    return { success: false, error: error.message };
  }

  return { success: true, feedback: data };
}

/**
 * Fetch existing feedback for a job.
 */
export async function fetchClientFeedback(jobId) {
  if (!jobId) return null;

  const { data, error } = await supabase.from('client_feedback')
    .select('*')
    .eq('job_id', jobId)
    .order('date', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.warn('[clientService] fetchClientFeedback failed:', error.message);
    return null;
  }

  return data;
}

// ─── Client Portal user linkage (Phase 5.75-vocab Commit A, 2026-04-26) ────
// Wraps the SECURITY DEFINER RPCs from migration 267. See
// docs/backbone/phase-5-75-vocab-followup-context-brief.md for design.
//
// Why these are needed: prior to Commit A there was no UI for admin/SPL
// to grant or revoke a portal user's access to a Client. Linkage was
// implicit via email match on `client.contact_email`, which migration 261
// flagged as a security liability. These methods + the admin UI in Slice 4
// give the explicit grant/revoke flow that universal SMB B2B SaaS uses.

/**
 * List active portal users for a Client (admin/SPL only). Returns the
 * shape `fetch_client_portal_users` produces.
 */
export async function fetchClientPortalUsers(clientId) {
  if (!clientId) return [];
  const { data, error } = await supabase.rpc('fetch_client_portal_users', { p_client_id: clientId });
  if (error) {
    console.error('[clientService] fetchClientPortalUsers error:', error.message);
    return [];
  }
  return data || [];
}

/**
 * Build the HTML + text body for a client portal invite email. Inline
 * styles only (most email clients strip CSS). Tone: warm, professional,
 * recipient-first. The recipient may not have heard of Juuni, so the
 * message leads with the inviter and the client they'll get access to —
 * not our brand.
 */
function buildClientInviteEmail({ recipientName, _recipientEmail, clientName, inviterName, magicLinkUrl }) {
  const greeting = recipientName ? `Hi ${recipientName},` : 'Hi,';
  const inviterLine = inviterName
    ? `${inviterName} has set up a Client Portal so you can track <strong>${clientName}</strong>'s annotation projects.`
    : `You've been invited to a Client Portal so you can track <strong>${clientName}</strong>'s annotation projects.`;
  const inviterLineText = inviterName
    ? `${inviterName} has set up a Client Portal so you can track ${clientName}'s annotation projects.`
    : `You've been invited to a Client Portal so you can track ${clientName}'s annotation projects.`;

  const html = `
<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Accept your Client Portal invitation</title></head>
<body style="margin:0; padding:0; background-color:#f6f6f6; font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f6f6f6; padding:24px 0;">
    <tr><td align="center">
      <table width="560" cellpadding="0" cellspacing="0" border="0" style="background-color:#ffffff; border-radius:8px; padding:32px;">
        <tr><td>
          <p style="margin:0 0 16px; font-size:14px; color:#333; line-height:1.5;">${greeting}</p>
          <p style="margin:0 0 24px; font-size:14px; color:#333; line-height:1.6;">${inviterLine}</p>
          <p style="margin:0 0 24px; font-size:13px; color:#555; line-height:1.6;">In the portal you'll see project status, delivered batches, and quality reports — and you can sign off on deliveries when they're ready.</p>
          <table cellpadding="0" cellspacing="0" border="0" style="margin:0 auto 24px;">
            <tr><td style="background-color:#5DCAA5; border-radius:6px;">
              <a href="${magicLinkUrl}" style="display:inline-block; padding:12px 28px; font-size:14px; font-weight:600; color:#ffffff; text-decoration:none; font-family:-apple-system,BlinkMacSystemFont,sans-serif;">Accept invitation</a>
            </td></tr>
          </table>
          <p style="margin:0 0 16px; font-size:11px; color:#888; line-height:1.5; text-align:center;">This link expires in 24 hours.</p>
          <hr style="border:none; border-top:1px solid #eee; margin:24px 0;">
          <p style="margin:0 0 8px; font-size:11px; color:#888; line-height:1.5;">If the button doesn't work, copy and paste this link into your browser:</p>
          <p style="margin:0 0 16px; font-size:11px; color:#666; word-break:break-all; font-family:'SF Mono',Menlo,Consolas,monospace;">${magicLinkUrl}</p>
          <p style="margin:24px 0 0; font-size:11px; color:#aaa; line-height:1.5;">If you weren't expecting this email, you can safely ignore it — no account will be created until you click the link.</p>
        </td></tr>
      </table>
      <p style="margin:16px 0 0; font-size:10px; color:#aaa;">Sent by <a href="https://juuniai.com" style="color:#aaa; text-decoration:underline;">Juuni AI</a> on behalf of ${clientName}'s annotation team</p>
    </td></tr>
  </table>
</body></html>`;

  const text = `${greeting}

${inviterLineText}

In the portal you'll see project status, delivered batches, and quality reports — and you can sign off on deliveries when they're ready.

Accept invitation: ${magicLinkUrl}

This link expires in 24 hours.

If the button doesn't work, copy and paste the link above into your browser.

If you weren't expecting this email, you can safely ignore it — no account will be created until you click the link.

—
Sent by Juuni AI on behalf of ${clientName}'s annotation team`;

  return { html, text };
}

/**
 * Send a client invite email via the send-email edge function. Logs but
 * does not throw on failure — the membership row is already created at
 * this point (RPC succeeded), so an email-send hiccup shouldn't roll back
 * the linkage. Admin can re-trigger send via re-invite.
 */
async function sendClientInviteEmail({ token, recipientEmail, recipientName, clientName, inviterName }) {
  // Compute base URL for the magic link. Same origin as the page so dev
  // and staging both work without env vars.
  const baseUrl = typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : 'https://staging.juuniai.com';
  const magicLinkUrl = `${baseUrl}/client/accept/${encodeURIComponent(token)}`;
  const { html, text } = buildClientInviteEmail({ recipientName, _recipientEmail: recipientEmail, clientName, inviterName, magicLinkUrl });

  const subject = inviterName
    ? `${inviterName} invited you to ${clientName}'s Client Portal`
    : `You've been invited to ${clientName}'s Client Portal`;

  try {
    const { data, error } = await supabase.functions.invoke('send-email', {
      body: {
        to: recipientEmail,
        subject,
        html,
        text,
      },
    });
    if (error) {
      console.warn('[clientService] sendClientInviteEmail invoke error:', error.message);
      return { success: false, error: error.message };
    }
    if (!data?.success) {
      console.warn('[clientService] sendClientInviteEmail backend rejected:', data?.error);
      return data || { success: false, error: 'Email send failed' };
    }
    return { success: true, messageId: data.messageId, magicLinkUrl };
  } catch (err) {
    console.error('[clientService] sendClientInviteEmail threw:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Add a portal user to a Client. Generates an access_token if `sendInvite`.
 * Returns `{ success, reviewer_id, access_token, auto_linked_individual_id, send_invite, client_name }`
 * on success, or `{ success: false, error }` on failure (duplicate email,
 * client not found, cross-tenant, etc.).
 *
 * Audit-logs LINK_CLIENT_USER (or INVITE_CLIENT_USER if invite sent).
 *
 * Side effect: if `sendInvite` is true, fires a magic-link email via the
 * `send-email` edge function. The email send is best-effort — if it fails,
 * the membership row stays (admin can re-invite via UI) and we surface
 * `email_send_error` in the result so the UI can show a non-fatal warning.
 */
export async function addClientPortalUser({
  clientId,
  name,
  email,
  role = 'reviewer',
  sendInvite = true,
  inviterName,                       // optional — admin's display name to personalize the invite
}) {
  if (!clientId || !email?.trim()) {
    return { success: false, error: 'Client ID and email are required' };
  }
  const { data, error } = await supabase.rpc('add_client_portal_user', {
    p_client_id: clientId,
    p_name: (name || '').trim() || email.trim(),
    p_email: email.trim(),
    p_role: role,
    p_send_invite: !!sendInvite,
  });
  if (error) {
    console.error('[clientService] addClientPortalUser error:', error.message);
    return { success: false, error: error.message };
  }
  if (!data?.success) {
    // RPC returned a structured error (e.g., duplicate email)
    return data;
  }

  // Audit-log on successful add. The RPC itself does not write to
  // admin_audit_log — the convention in this repo is service-layer audit
  // for caller-context provenance (covers who *initiated* the action,
  // even if the RPC was the one that wrote the row).
  logAudit(
    sendInvite ? AUDIT_ACTIONS.INVITE_CLIENT_USER : AUDIT_ACTIONS.LINK_CLIENT_USER,
    {
      entityType: 'client_reviewer',
      entityId: data.reviewer_id,
      details: {
        client_id: clientId,
        client_name: data.client_name,
        reviewer_email: email.trim(),
        reviewer_name: name?.trim() || null,
        reviewer_role: role,
        send_invite: !!sendInvite,
        auto_linked_individual_id: data.auto_linked_individual_id || null,
      },
    },
  );

  // Fire the invite email if requested (best-effort).
  let emailSendResult = null;
  if (sendInvite && data.access_token) {
    emailSendResult = await sendClientInviteEmail({
      token: data.access_token,
      recipientEmail: email.trim(),
      recipientName: name?.trim() || null,
      clientName: data.client_name || 'your team',
      inviterName: inviterName || null,
    });
  }

  return {
    ...data,
    email_send_result: emailSendResult,
  };
}

/**
 * Revoke a portal user's access to a Client. Soft-revoke: sets active=false,
 * removed_at, removed_by; clears access_token (so any pending invite link
 * stops working).
 *
 * Audit-logs REVOKE_CLIENT_USER.
 */
export async function revokeClientPortalUser(reviewerId, { clientId, reviewerEmail, reviewerName } = {}) {
  if (!reviewerId) return { success: false, error: 'Reviewer ID required' };
  const { data, error } = await supabase.rpc('revoke_client_portal_user', { p_reviewer_id: reviewerId });
  if (error) {
    console.error('[clientService] revokeClientPortalUser error:', error.message);
    return { success: false, error: error.message };
  }
  if (!data?.success) return data;

  logAudit(AUDIT_ACTIONS.REVOKE_CLIENT_USER, {
    entityType: 'client_reviewer',
    entityId: reviewerId,
    details: {
      client_id: clientId || null,
      reviewer_email: reviewerEmail || data.reviewer_email || null,
      reviewer_name: reviewerName || null,
    },
  });

  return data;
}

/**
 * Validate an invite token without redeeming it. Used by the
 * /client/accept/<token> landing page to render context (which Client
 * are you accepting?) before signup.
 *
 * Returns `null` if the token is missing/malformed/invalid; otherwise
 * returns `{ reviewer_id, client_id, client_name, reviewer_email,
 * reviewer_name, invite_sent_at, is_expired, is_redeemed }`.
 *
 * The RPC is callable by anonymous users (no L1+L2+L3 gating) — token
 * possession IS the only authorization check at this stage.
 */
export async function peekClientInvite(token) {
  if (!token || typeof token !== 'string') return null;
  const { data, error } = await supabase.rpc('peek_client_invite', { p_token: token });
  if (error) {
    console.error('[clientService] peekClientInvite error:', error.message);
    return null;
  }
  if (!Array.isArray(data) || data.length === 0) return null;
  return data[0];
}

/**
 * Redeem a client invite. Caller MUST be authenticated as the email-matching
 * invitee (the RPC verifies email ownership server-side).
 *
 * Side effects (all server-side):
 *   - Sets client_reviewer.redeemed_by_individual_id to auth.uid()
 *   - Sets client_reviewer.redeemed_at to NOW()
 *   - Clears client_reviewer.access_token (single-use)
 *   - If individual row missing for auth.uid(), creates one with role='client'
 *   - Audit-logs ACCEPT_CLIENT_INVITE
 *
 * Returns `{ success: true, reviewer_id, client_id, individual_id }` on
 * success or `{ success: false, error }` on failure.
 */
export async function redeemClientInvite(token) {
  if (!token) return { success: false, error: 'Token required' };
  const { data, error } = await supabase.rpc('redeem_client_invite', { p_token: token });
  if (error) {
    console.error('[clientService] redeemClientInvite error:', error.message);
    return { success: false, error: error.message };
  }
  if (!data?.success) return data;

  logAudit(AUDIT_ACTIONS.ACCEPT_CLIENT_INVITE, {
    entityType: 'client_reviewer',
    entityId: data.reviewer_id,
    details: {
      client_id: data.client_id,
      individual_id: data.individual_id,
    },
  });

  return data;
}
